/**
 * Underpowered-decoder auto-downgrade: the tier/fps decision for a decode-lag episode.
 *
 * When a guest's decoder genuinely cannot keep up (sustained decode-lag episode), the host
 * must drop that guest to the 360p ShadowCam (bottom tier), and — ONLY when the decoder is
 * failing to sustain the send rate — step framerate down 60→45→30 before the floor, so an
 * underpowered device gets a playable stream instead of a stalling one. Silent (no HUD
 * notice). The existing one-tier recovery demotion stays as-is; this is the live mid-session
 * downgrade that doesn't wait for a drop.
 *
 * FPS IS NOT A LATENCY LEVER. A hardware decoder holds a fixed number of frames in flight,
 * so a standing backlog costs `frames / fps` of latency: halving the framerate doubles the
 * wait behind the same depth. The fps ladder therefore steps only on a THROUGHPUT deficit
 * (`hasDecodeThroughputDeficit` — decoder well under the rate it is sent), where fewer frames
 * per second is what lets it catch up.
 *
 * STANDING DEPTH IS NOT A DECODE LAG. A depth-only episode — the decoder keeping pace at
 * 59/60 fps behind a queue of frames — used to take the 360p tier "for headroom". Field data
 * (Android, 2026-09-19, sessions 84382f4a and f59cdd34) showed the opposite: the resolution
 * switch reconfigures the Android decoder and roughly DOUBLED the standing buffer (~21 frames
 * -> ~320 ms at 60 fps). Only a measured throughput deficit may change tier or fps; a
 * depth-only episode yields NO change (null) and is left to the standing-latency drain.
 */

import { hasDecodeThroughputDeficit, PRESENT_DEFICIT_RATIO, type DecodeLagEpisode } from './decodeLagContracts';
import type { QualityTier } from './webrtcLatencyContracts';

/** The 360p tier (ShadowCam00 = 640x360). */
export const DECODE_LAG_TARGET_TIER = 'bottom' as const;

/** Framerate ladder: 60 → 45 → 30 before the floor. */
export const DECODE_LAG_FPS_LADDER = [60, 45, 30] as const;

/**
 * Host callback seam: PCM invokes this once per decode-lag episode so PeerCoordinator can
 * run the real shared-capture swap to ShadowCam00. Type-only contract for Phases 3/4.
 */
export type DecodeLagTierDemotionHandler = (
  peerId: string,
  tier: typeof DECODE_LAG_TARGET_TIER,
) => void;

/**
 * The wire message the host sends a guest to switch it to the 360p tier + a lower fps.
 * Silent (no HUD); the guest re-captures at the new tier/fps.
 */
export type DecodeLagTierChange = {
  type: 'decodeLagTierChange';
  /** Always the 360p tier (ShadowCam00). */
  tier: 'bottom';
  /**
   * The fps to send after this change: a ladder step (45, then 30) on a throughput deficit,
   * or the peer's CURRENT fps when the deficit is already at the ladder floor and only the
   * tier moves.
   */
  targetFramerate: number;
  /**
   * Why the framerate is what it is. `decodeLagTierChangeFor` only ever produces
   * `throughput-deficit` since the standing-latency work; `depth-only-hold` remains on the
   * wire guard so a message from an older host is still recognised, never fabricated anew.
   */
  framerateReason: 'throughput-deficit' | 'depth-only-hold';
};

export function isDecodeLagTierChange(v: unknown): v is DecodeLagTierChange {
  if (typeof v !== 'object' || v === null) return false;
  const e = v as Record<string, unknown>;
  const fps = e['targetFramerate'];
  return (
    e['type'] === 'decodeLagTierChange' &&
    e['tier'] === 'bottom' &&
    typeof fps === 'number' &&
    Number.isFinite(fps) &&
    fps > 0 &&
    fps <= DECODE_LAG_FPS_LADDER[0] &&
    (e['framerateReason'] === 'throughput-deficit' || e['framerateReason'] === 'depth-only-hold')
  );
}

export const DECODE_LAG_TIER_CHANGE_MESSAGE_TYPE = 'decodeLagTierChange' as const;

/** The next rung below `currentFps` on the ladder, or null at the floor. */
function nextFpsStepBelow(currentFps: number): 45 | 30 | null {
  if (currentFps > 45) return 45;
  if (currentFps > 30) return 30;
  return null;
}

/**
 * The downgrade decision for a guest with an active decode-lag episode.
 *
 * Returns null when no downgrade is warranted: no active episode, a DEPTH-ONLY episode (the
 * decoder is keeping pace — lowering resolution reconfigures it and lengthens the queue; see
 * the module header), or already on the 360p tier at the fps floor. Otherwise the decoder has
 * a measured throughput deficit: the tier is `bottom` (360p) and the fps steps one rung down
 * the ladder (60→45→30), holding at the current value only once the ladder is exhausted.
 *
 * `currentFps` and `deliveredFps` are different questions and must not be collapsed:
 * `currentFps` is where this peer sits on the ladder (which rung to step to), `deliveredFps`
 * is what the encoder measurably sent (whether the decoder is behind at all). Using the
 * configured rate for both makes the deficit test self-confirming — each step down lowers
 * the rate the next tick is judged against, so the ladder walks itself to the floor.
 */
export function decodeLagTierChangeFor(
  currentTier: QualityTier,
  currentFps: number,
  episode: DecodeLagEpisode | null,
  deliveredFps: number = currentFps,
): DecodeLagTierChange | null {
  if (!episode || !episode.active) return null;

  // PRESENTATION deficit (Safari-class): frames decode fine and die at the render deadline.
  // The remedy is the smaller tier — smaller frames arrive sooner — and NEVER an fps step,
  // which cannot change how long a late frame takes to arrive. Holding currentFps is the
  // depth-only-hold analogue: the wire guard keeps accepting it for older-host symmetry.
  // The ratio is re-checked here (not just trusted from the producer) — the episode rides
  // an untrusted wire, and demotion must never fire on a healthy presented rate.
  if (episode.presentedFps !== undefined) {
    const presentDeficit = episode.presentedFps < episode.decodeFps * PRESENT_DEFICIT_RATIO;
    if (!presentDeficit || currentTier === 'bottom') return null;
    return {
      type: DECODE_LAG_TIER_CHANGE_MESSAGE_TYPE,
      tier: 'bottom',
      targetFramerate: currentFps,
      framerateReason: 'depth-only-hold',
    };
  }

  // Depth-only: the decoder keeps pace. No tier change, no fps step — nothing here helps.
  if (!hasDecodeThroughputDeficit(episode, deliveredFps)) return null;
  const step = nextFpsStepBelow(currentFps);

  if (currentTier === 'bottom') {
    // Already on 360p: the only lever left is fps; at the ladder floor there is nothing left.
    if (step === null) return null;
    return {
      type: DECODE_LAG_TIER_CHANGE_MESSAGE_TYPE,
      tier: 'bottom',
      targetFramerate: step,
      framerateReason: 'throughput-deficit',
    };
  }
  // Not on the floor: drop to 360p and step fps one rung (or hold it at the ladder floor).
  return {
    type: DECODE_LAG_TIER_CHANGE_MESSAGE_TYPE,
    tier: 'bottom',
    targetFramerate: step ?? currentFps,
    framerateReason: 'throughput-deficit',
  };
}
